import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
} from '../../../../_metronic/_partials/controls';
import {
  Button,
  TextField,
  MuiThemeProvider,
  createMuiTheme,
  FormHelperText,
} from '@material-ui/core';
import Select from 'react-select';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import {
  deleteUser,
  getUserById,
  postUser,
  updateUser,
} from '../../../../api/user';
import { getClubs } from '../../../../api/club'; // <-- NUEVO
import { useSkeleton } from '../../../hooks/useSkeleton';
import { alertError, alertSuccess } from '../../../../utils/logger';
import ConfirmDialog from '../../../components/dialogs/ConfirmDialog';

// Create theme for delete button (red)
const theme = createMuiTheme({
  palette: {
    secondary: { main: '#F64E60' },
  },
});

function getEmptyAdmin() {
  return {
    fullName: '',
    email: '',
    role: '',
    password: '',
    repeatPassword: '',
    // nuevos campos condicionales
    club: null,          // club_manager
  };
}

const ALL_ROLE_OPTIONS = [
  { label: 'admin', value: 'admin' },
  { label: 'club_manager', value: 'club_manager' },
  { label: 'judge', value: 'judge' },
];

export default function EditAdminsPage() {
  const [admin, setAdmin] = useState(getEmptyAdmin());
  const [clubs, setClubs] = useState([]);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const adminId = useParams().id;
  const history = useHistory();
  const location = useLocation();

  const {
    isLoading: isLoadingData,
    disableLoading: disableLoadingData,
    ContentSkeleton,
  } = useSkeleton();

  // === NUEVO: leer ?view=judges|staff (por defecto staff) ===
  const view = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const v = params.get('view');
    return v === 'judges' ? 'judges' : 'staff';
  }, [location.search]);

  // === Roles visibles según view ===
  const ROLE_OPTIONS = useMemo(() => {
    return view === 'judges'
      ? ALL_ROLE_OPTIONS.filter(r => r.value === 'judge')
      : ALL_ROLE_OPTIONS.filter(r => r.value === 'admin' || r.value === 'club_manager');
  }, [view]);

  // Carga inicial (si edit)
  useEffect(() => {
    if (!adminId) {
      // Preselecciona rol por vista en alta
      setAdmin(prev => ({
        ...prev,
        role: view === 'judges' ? 'judge' : 'admin',
      }));
      disableLoadingData();
      return;
    }
    getUserById(adminId)
      .then((res) => {
        if (res.status === 200) {
          const user = res.data;
          delete user.password; // no mostrar hash

          setAdmin({
            ...getEmptyAdmin(),
            ...user,
            club: user.club || null,
          });
          disableLoadingData();
        }
      })
      .catch((error) => {
        alertError({ error: error, customMessage: 'Could not get user.' });
        history.push(`/admins${location.search}`);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminId, disableLoadingData, history, location.search, view]);

  // Cargar opciones de selects (una vez)
  useEffect(() => {
    getClubs()
      .then((res) => {
        if (res.status === 200) setClubs(res.data || []);
      })
      .catch((error) => {
        alertError({ error, customMessage: 'Could not get clubs.' });
      });
  }, []);

  function saveAdmin() {
    if (admin.password !== admin.repeatPassword) {
      alertError({ error: null, customMessage: 'Passwords do not match.' });
      return;
    }

    // Asegura coherencia con la vista
    const allowed = ROLE_OPTIONS.map(r => r.value);
    if (!allowed.includes(admin.role)) {
      alertError({
        error: null,
        customMessage:
          view === 'judges'
            ? 'From Judges you can only create/edit judges.'
            : 'From Administrators you can only create/edit admins and club managers.',
      });
      return;
    }

    // Validaciones mínimas
    if (!admin.fullName?.trim()?.length) {
      alertError({ error: null, customMessage: 'Name is required.' });
      return;
    }
    if (!admin.email?.trim()?.length) {
      alertError({ error: null, customMessage: 'Email is required.' });
      return;
    }
    if (!admin.role) {
      alertError({ error: null, customMessage: 'Role is required.' });
      return;
    }
    if (!adminId && !admin.password) {
      alertError({ error: null, customMessage: 'Password is required.' });
      return;
    }
    if (admin.role === 'club_manager' && !admin.club) {
      alertError({ error: null, customMessage: 'Club is required for a club manager.' });
      return;
    }

    // Construir payload limpio según rol
    const base = {
      fullName: admin.fullName,
      email: admin.email,
      role: admin.role,
    };
    if (admin.password) base.password = admin.password;

    let payload = { ...base };

    if (admin.role === 'club_manager') {
      payload.club = admin.club || null;
    } else if (admin.role === 'judge') {
      const ids = (admin.regattas || []).map(r => (typeof r === 'string' ? r : r?._id || r?.value));
      payload.club = null;
    } else {
      // admin
      payload.club = null;
    }

    const req = adminId ? updateUser(adminId, payload) : postUser(payload);

    req
      .then((res) => {
        const ok = adminId ? res.status === 200 : res.status === 201;
        if (ok) {
          alertSuccess({
            title: 'Saved!',
            customMessage: adminId
              ? 'Changes successfully saved.'
              : 'User successfully created.',
          });
          history.push(`/admins${location.search}`); // volver preservando ?view=
        }
      })
      .catch((error) => {
        alertError({
          error,
          customMessage: adminId ? 'Could not save changes.' : 'Could not save user.',
        });
      });
  }

  const handleChange = (element) => (event) => {
    setAdmin({ ...admin, [element]: event.target.value });
  };

  const handleChangeRole = (new_role) => {
    const value = new_role?.value || '';
    setAdmin((prev) => ({
      ...prev,
      role: value,
      club: value === 'club_manager' ? prev.club : null,
    }));
  };

  const handleChangeClub = (new_club) => {
    setAdmin((prev) => ({
      ...prev,
      club: new_club?.value || null,
    }));
  };

  // Opciones memoizadas
  const roleValue = useMemo(() => {
    return ROLE_OPTIONS.find((r) => r.value === admin.role) || null;
  }, [admin.role, ROLE_OPTIONS]);

  const clubOptions = useMemo(
    () =>
      (clubs || []).map((c) => ({
        label: c.name || c.fullName || c.title || c._id,
        value: c._id,
      })),
    [clubs]
  );

  const clubValue = useMemo(() => {
    if (!admin.club) return null;
    return clubOptions.find((o) => o.value === admin.club) || null;
  }, [admin.club, clubOptions]);

  if (isLoadingData) return <ContentSkeleton />;

  return (
    <>
      <Card>
        <CardHeader title='Edit user' />
        <CardBody>
          <TextField
            id={`fullName`}
            label='Name'
            value={admin.fullName}
            onChange={handleChange('fullName')}
            InputLabelProps={{ shrink: true }}
            margin='normal'
            variant='outlined'
            required
          />

          <TextField
            id={`email`}
            label='Email'
            value={admin.email}
            onChange={handleChange('email')}
            InputLabelProps={{ shrink: true }}
            margin='normal'
            variant='outlined'
            required
          />

          <TextField
            id={`password`}
            label='Password'
            value={admin.password}
            onChange={handleChange('password')}
            InputLabelProps={{ shrink: true }}
            type='password'
            margin='normal'
            variant='outlined'
            required={!adminId}
          />
          <TextField
            id={`repeatPassword`}
            label='Repeat password'
            value={admin.repeatPassword}
            onChange={handleChange('repeatPassword')}
            InputLabelProps={{ shrink: true }}
            type='password'
            margin='normal'
            variant='outlined'
            required={!adminId}
          />

          <Select
            options={ROLE_OPTIONS}
            value={roleValue}
            onChange={handleChangeRole}
            isClearable
            isSearchable
            placeholder={`Select role...`}
            className='mb-3 mt-3'
          />
          <FormHelperText>Select a Role</FormHelperText>

          {admin.role === 'club_manager' && (
            <>
              <Select
                options={clubOptions}
                value={clubValue}
                onChange={handleChangeClub}
                isClearable
                isSearchable
                placeholder={`Select club...`}
                className='mb-3 mt-3'
              />
              <FormHelperText>Select a Club</FormHelperText>
            </>
          )}
        </CardBody>
      </Card>

      <Button
        onClick={() => history.push(`/admins${location.search}`)}
        variant='outlined'
        style={{ marginRight: '20px' }}
      >
        Back
      </Button>
      <Button
        onClick={saveAdmin}
        variant='outlined'
        color='primary'
        style={{ marginRight: '20px' }}
      >
        Save user
      </Button>

      {adminId && (
        <>
          <MuiThemeProvider theme={theme}>
            <Button
              onClick={() => setOpenConfirmDialog(true)}
              variant='outlined'
              color='secondary'
            >
              Delete user
            </Button>
          </MuiThemeProvider>

          <ConfirmDialog
            title={'Are you sure you want to delete this user?'}
            open={openConfirmDialog}
            setOpen={setOpenConfirmDialog}
            onConfirm={() => {
              deleteUser(adminId)
                .then((res) => {
                  if (res.status === 204) {
                    alertSuccess({
                      title: 'Deleted!',
                      customMessage: 'User deleted successfully',
                    });
                    history.push(`/admins${location.search}`);
                  }
                })
                .catch((error) => {
                  alertError({
                    error: error,
                    customMessage: 'Could not delete user.',
                  });
                });
            }}
          />
        </>
      )}
    </>
  );
}
